import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  CircularProgress,
  TextFieldProps,
} from '@mui/material';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import { useSnackbar } from 'notistack';
import bcraService from '../../services/bcraService';
import type { BcraCentralDeudoresConsulta, BcraNivelRiesgo } from '../../types/bcraCentralDeudores';
import {
  cuitConsultable,
  colorIconoPorNivelRiesgo,
  mensajeErrorConsultaBcra,
  normalizarCuitConsulta,
  snackbarVariantPorNivelRiesgo,
} from '../../utils/bcraCentralDeudores';
import BcraCentralDeudoresDialog from './BcraCentralDeudoresDialog';

export type CuitBcraCheckFieldProps = Omit<TextFieldProps, 'value' | 'onChange'> & {
  value: string;
  onChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  /** Si true, consulta al completar 11 dígitos. Por defecto false (click en icono). */
  consultaAutomatica?: boolean;
  /** Invocado al obtener o perder resultado BCRA (cambio de CUIT). */
  onBcraConsulta?: (result: BcraCentralDeudoresConsulta | null) => void;
};

export const CuitBcraCheckField: React.FC<CuitBcraCheckFieldProps> = ({
  value,
  onChange,
  InputProps,
  consultaAutomatica = false,
  onBcraConsulta,
  ...textFieldProps
}) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [ultimoResultado, setUltimoResultado] = useState<BcraCentralDeudoresConsulta | null>(null);
  const [ultimoCuitConsultado, setUltimoCuitConsultado] = useState<string | null>(null);
  const [nivelIcono, setNivelIcono] = useState<BcraNivelRiesgo | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const consultandoRef = useRef(false);
  /** Evita reintentos automáticos en el mismo CUIT tras éxito o error. */
  const ultimoIntentoCuitRef = useRef<string | null>(null);

  const cuitNorm = normalizarCuitConsulta(value);
  const puedeConsultar = cuitConsultable(value);

  useEffect(() => {
    if (ultimoCuitConsultado && ultimoCuitConsultado !== cuitNorm) {
      setUltimoResultado(null);
      setUltimoCuitConsultado(null);
      setNivelIcono(null);
      ultimoIntentoCuitRef.current = null;
      onBcraConsulta?.(null);
    }
  }, [cuitNorm, ultimoCuitConsultado, onBcraConsulta]);

  useEffect(() => {
    if (ultimoIntentoCuitRef.current && ultimoIntentoCuitRef.current !== cuitNorm) {
      ultimoIntentoCuitRef.current = null;
    }
  }, [cuitNorm]);

  const mostrarSnackbar = useCallback((data: BcraCentralDeudoresConsulta) => {
    const variant = snackbarVariantPorNivelRiesgo(data.nivelRiesgo);
    const key = enqueueSnackbar(data.mensajeResumen, {
      variant,
      autoHideDuration: 6000,
      action: (snackbarKey) => (
        <button
          type="button"
          style={{
            background: 'none',
            border: 'none',
            color: 'inherit',
            cursor: 'pointer',
            textDecoration: 'underline',
            fontSize: '0.875rem',
          }}
          onClick={() => {
            closeSnackbar(snackbarKey);
            setModalOpen(true);
          }}
        >
          Ver informe
        </button>
      ),
    });
    return key;
  }, [closeSnackbar, enqueueSnackbar]);

  const ejecutarConsulta = useCallback(async () => {
    if (!puedeConsultar || consultandoRef.current) return;

    consultandoRef.current = true;
    ultimoIntentoCuitRef.current = cuitNorm;
    setLoading(true);
    try {
      const data = await bcraService.consultarCentralDeudores(cuitNorm);
      setUltimoResultado(data);
      setUltimoCuitConsultado(cuitNorm);
      setNivelIcono(data.nivelRiesgo);
      onBcraConsulta?.(data);
      mostrarSnackbar(data);
    } catch (error) {
      setUltimoResultado(null);
      setNivelIcono(null);
      setUltimoCuitConsultado(null);
      onBcraConsulta?.(null);
      enqueueSnackbar(mensajeErrorConsultaBcra(error), { variant: 'error' });
    } finally {
      setLoading(false);
      consultandoRef.current = false;
    }
  }, [cuitNorm, enqueueSnackbar, mostrarSnackbar, onBcraConsulta, puedeConsultar]);

  useEffect(() => {
    if (!consultaAutomatica || !puedeConsultar || loading || consultandoRef.current) return;
    if (ultimoIntentoCuitRef.current === cuitNorm) return;
    void ejecutarConsulta();
  }, [consultaAutomatica, cuitNorm, puedeConsultar, ejecutarConsulta, loading]);

  const handleIconClick = useCallback(() => {
    if (!puedeConsultar || loading) return;

    if (ultimoResultado && ultimoCuitConsultado === cuitNorm) {
      setModalOpen(true);
      return;
    }

    void ejecutarConsulta();
  }, [cuitNorm, ejecutarConsulta, loading, puedeConsultar, ultimoCuitConsultado, ultimoResultado]);

  const iconColor = colorIconoPorNivelRiesgo(nivelIcono);

  const tooltipTitle = !puedeConsultar
    ? consultaAutomatica
      ? 'Ingrese un CUIT de 11 dígitos para consultar automáticamente'
      : 'Ingrese un CUIT de 11 dígitos para consultar'
    : loading
      ? 'Consultando Central de Deudores (BCRA)...'
      : ultimoResultado && ultimoCuitConsultado === cuitNorm
        ? 'Ver informe BCRA'
        : ultimoIntentoCuitRef.current === cuitNorm
          ? 'Reintentar consulta BCRA'
          : consultaAutomatica
            ? 'Consulta automática al completar el CUIT'
            : 'Consultar Central de Deudores (BCRA)';

  return (
    <>
      <TextField
        {...textFieldProps}
        value={value}
        onChange={onChange}
        InputProps={{
          ...InputProps,
          endAdornment: (
            <>
              {InputProps?.endAdornment}
              <InputAdornment position="end">
                <Tooltip title={tooltipTitle}>
                  <span>
                    <IconButton
                      onClick={handleIconClick}
                      disabled={!puedeConsultar || loading}
                      edge="end"
                      size="small"
                      color={iconColor}
                      aria-label="Consultar BCRA"
                    >
                      {loading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <GppMaybeIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </InputAdornment>
            </>
          ),
        }}
      />
      <BcraCentralDeudoresDialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        data={ultimoResultado}
      />
    </>
  );
};

export default CuitBcraCheckField;
